import React, { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { clearToken, getToken, stopTokenTimer } from "../Services/tokenUtils";
import API from "../Services/BaseUrl";
import {
  FaHome, FaBuilding, FaStore, FaUsers, FaUserFriends, FaTags, FaBox,
  FaWarehouse, FaList, FaTruckLoading, FaTruck, FaCogs, FaTools,
  FaBriefcase, FaUserShield, FaKey, FaClipboardList, FaSignOutAlt,
  FaTimes, FaLock, FaShoppingCart, FaClipboardCheck, FaFileSignature,
  FaChevronDown, FaChevronRight, FaImages,
} from "react-icons/fa";
import { APP_VERSION } from "../version";

/**
 * Sidebar — NavLink-driven navigation.
 *
 * Each entry maps to a URL. Active highlight is automatic via NavLink's
 * isActive callback (no polling). Permission filter still hides items the
 * current user cannot access; the route-level <Guard> in App.js enforces
 * the same check defensively when a URL is pasted.
 */

const MENU = [
  { id: "dashboard",          label: "Dashboard",          icon: FaHome,          path: "/dashboard",          permission: null },
  { id: "company",            label: "Company",            icon: FaBuilding,      path: "/company",            permission: "companyAccess" },
  { id: "stores",             label: "Stores",             icon: FaStore,         path: "/stores",             permission: "storesAccess" },
  { id: "vendors",            label: "Vendors",            icon: FaUsers,         path: "/vendors",            permission: "vendorsAccess" },
  { id: "customers",          label: "Customers",          icon: FaUserFriends,   path: "/customers",          permission: "customerAccess" },
  { id: "categories",         label: "Categories",         icon: FaTags,          path: "/categories",         permission: "categoriesAccess" },
  { id: "products",           label: "Products",           icon: FaBox,           path: "/products",           permission: "productsAccess" },
  { id: "gallery",            label: "Product Gallery",    icon: FaImages,        path: "/gallery",            permission: "productsAccess" },
  { id: "inventory",          label: "Inventory",          icon: FaWarehouse,     path: "/inventory",          permission: "materialAccess",
    // Stock-type quick views. The values are the exact UI status keys the
    // Inventory filter already uses (see Inventory.jsx STOCK_ENUM); each link
    // navigates to /inventory?stock=… which the Inventory page reads and applies
    // as the sole stock facet. "All" (no param) clears the stock filter.
    children: [
      { label: "All",                to: "/inventory" },
      { label: "Normal Stock",       to: "/inventory?stock=NORMAL" },
      { label: "Low Stock",          to: "/inventory?stock=LOW" },
      { label: "Zero Low Stock",     to: "/inventory?stock=LOW_ZERO" },
      { label: "Over Stock",         to: "/inventory?stock=OVERSTOCK" },
      { label: "Cost Pending Stock", to: "/inventory?stock=COST_PENDING" },
    ] },
  { id: "inventory-tracking", label: "Inventory Tracking", icon: FaList,          path: "/inventory-tracking", permission: "inventoryAccess" },
  { id: "stock-in",           label: "Stock In",           icon: FaTruckLoading,  path: "/stock-in",           permission: "stockAccess" },
  { id: "stock-out",          label: "Stock Out",          icon: FaTruck,         path: "/stock-out",          permission: "stockOutAccess" },
  { id: "purchase",           label: "Purchase Orders",    icon: FaShoppingCart,  path: "/purchase",           permission: "purchaseAccess" },
  { id: "material-requirement", label: "Material Requirement", icon: FaClipboardCheck, path: "/material-requirement", permission: "materialRequirementAccess" },
  { id: "feeder",             label: "Feeder Management",  icon: FaTools,         path: "/feeder",             permission: "feederAccess" },
  { id: "bom",                label: "BOM Management",     icon: FaCogs,          path: "/bom",                permission: "bomAccess" },
  { id: "orders",             label: "Sales Order",        icon: FaFileSignature, path: "/orders",             permission: "orderAccess" },
  { id: "jobs",               label: "Job Management",     icon: FaBriefcase,     path: "/jobs",               permission: "jobManagementAccess",
    // Status-scoped quick views. Groupings mirror the desktop rail submenu; see
    // the coverage table (all 15 statuses, mutually exclusive). Each navigates to
    // /jobs?statuses=… which the Job page reads via useUrlFilter.
    children: [
      { label: "All Jobs",     to: "/jobs" },
      { label: "Create",       to: "/jobs?statuses=CREATE" },
      { label: "In Progress",  to: "/jobs?statuses=START,PROCUREMENT,ASSEMBLY,PRODUCTION,TESTING" },
      { label: "Completed",    to: "/jobs?statuses=SITE_INSTALLATION,HOLD,REJECT,COMPLETED" },
      { label: "Accounting",   to: "/jobs?statuses=DONE,SALES_APPROVE,PACKING,DISPATCH,RETURN" },
    ] },
  { id: "user-management",    label: "User Management",    icon: FaUserShield,    path: "/user-management",    permission: "userManagementAccess" },
  { id: "userRoles",          label: "User Roles",         icon: FaKey,           path: "/user-roles",         permission: "userRolesAccess" },
  { id: "audit",              label: "Audit",              icon: FaClipboardList, path: "/audit",              permission: "auditAccess" },
  { id: "change-password",    label: "Change Password",    icon: FaLock,          path: "/change-password",    permission: null },
];

const EXPANDED_WIDTH = 220;
const COLLAPSED_WIDTH = 60;

export default function Sidebar({ isOpen, onClose, collapsed }) {
  const sidebarRef = useRef(null);
  const navigate = useNavigate();
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const permissions = JSON.parse(localStorage.getItem("userPermissions")) || {};

  useEffect(() => {
    function handleClickOutside(event) {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        if (isOpen) onClose();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose]);

  const handleLogoutConfirm = async () => {
    setLoggingOut(true);
    try {
      const token = getToken();
      if (token) {
        await API.post("/users/logout", null, { headers: { Authorization: token } });
      }
    } catch (error) {
      console.error("Logout API failed:", error);
    } finally {
      stopTokenTimer();
      clearToken();
      localStorage.setItem("logout", Date.now().toString());
      setShowLogoutDialog(false);
      setLoggingOut(false);
      navigate("/login");
    }
  };

  const desktopWidth = collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH;

  return (
    <>
      {isOpen && (
        <div onClick={onClose} className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" />
      )}

      <div
        ref={sidebarRef}
        style={{ width: desktopWidth }}
        className={`
          flex-shrink-0 h-full bg-[#3A3A3A] text-white shadow-xl z-50
          transition-all duration-300 overflow-hidden
          hidden lg:flex lg:flex-col
          fixed top-0 left-0 lg:static lg:top-auto lg:left-auto
          ${isOpen ? "!flex !flex-col w-64" : "lg:flex lg:flex-col"}
        `}
      >
        <div className="lg:hidden flex items-center justify-between px-3 py-3 bg-[#4682B4] flex-shrink-0">
          <span className="text-white font-semibold text-sm">Stock Management</span>
          <button onClick={onClose} className="text-white hover:opacity-80" aria-label="Close sidebar">
            <FaTimes size={18} />
          </button>
        </div>

        <ul className="flex-1 overflow-y-auto overflow-x-hidden py-3 px-2 space-y-1">
          {MENU
            .filter((item) => !item.permission || permissions[item.permission])
            .map((item) => (
              <SidebarMenuItem
                key={item.id}
                item={item}
                collapsed={collapsed && !isOpen}
                onNavigate={() => {
                  if (window.innerWidth < 1024) onClose();
                }}
              />
            ))}
        </ul>

        <div className="flex-shrink-0 pb-2 px-2">
          <button
            onClick={() => setShowLogoutDialog(true)}
            title="Logout"
            className="w-full py-2.5 bg-[#b44646] hover:bg-[#a94242] transition-colors text-white rounded-md text-sm flex items-center justify-center gap-2"
          >
            <FaSignOutAlt size={16} />
            {(!collapsed || isOpen) && (
              <span>{loggingOut ? "Logging out..." : "Logout"}</span>
            )}
          </button>
          {(!collapsed || isOpen) && (
            <p className="text-center text-xs text-gray-400 mt-1">v{APP_VERSION}</p>
          )}
        </div>
      </div>

      {showLogoutDialog && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[100]">
          <div className="bg-white rounded-lg shadow-lg p-6 w-80 text-center">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Confirm Logout</h2>
            <p className="text-gray-600 mb-6">Are you sure you want to logout?</p>
            <div className="flex justify-center gap-4">
              <button
                onClick={handleLogoutConfirm}
                disabled={loggingOut}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md disabled:opacity-50"
              >
                {loggingOut ? "Logging out..." : "Yes"}
              </button>
              <button
                onClick={() => setShowLogoutDialog(false)}
                disabled={loggingOut}
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-md disabled:opacity-50"
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Parse a comma-separated URL param value into a Set (order-independent).
function parseCsvParam(search, key) {
  const raw = new URLSearchParams(search || "").get(key);
  if (!raw) return new Set();
  return new Set(raw.split(",").map((s) => s.trim()).filter(Boolean));
}
function sameSet(a, b) {
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
}
// The filter param keys a submenu drives, derived from the children's own links
// (e.g. "statuses" for Job Management, "stock" for Inventory) — keeps the active
// highlight generic across different submenus and ignores unrelated params like
// page / q / pageSize that useUrlFilter may also put in the URL.
function childFilterKeys(children) {
  const keys = new Set();
  (children || []).forEach((c) => {
    const qs = c.to.includes("?") ? c.to.split("?")[1] : "";
    new URLSearchParams(qs).forEach((_v, k) => keys.add(k));
  });
  return keys;
}
// Segment-aware module match so a sibling whose path merely string-prefixes this
// one does not count as "inside" it (e.g. /inventory-tracking must NOT match the
// /inventory submenu).
function onModuleTree(pathname, base) {
  return pathname === base || pathname.startsWith(base + "/");
}

function SidebarMenuItem({ item, collapsed, onNavigate }) {
  const IconComponent = item.icon;
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const location = useLocation();
  const hasChildren = Array.isArray(item.children) && item.children.length > 0;
  const [open, setOpen] = useState(() => hasChildren && onModuleTree(location.pathname, item.path));

  // Auto-expand the submenu when the user is within this module.
  useEffect(() => {
    if (hasChildren && onModuleTree(location.pathname, item.path)) setOpen(true);
  }, [location.pathname, hasChildren, item.path]);

  // ---- Simple item (no submenu) — unchanged behavior. ----
  if (!hasChildren) {
    return (
      <li
        className="relative"
        onMouseEnter={() => collapsed && setTooltipVisible(true)}
        onMouseLeave={() => setTooltipVisible(false)}
      >
        <NavLink
          to={item.path}
          end={item.path === "/dashboard"}
          onClick={onNavigate}
          title={collapsed ? item.label : undefined}
          className={({ isActive }) => `
            w-full flex items-center gap-3 rounded-md cursor-pointer
            transition-all duration-150 text-left
            ${collapsed ? "justify-center px-0 py-2.5" : "px-3 py-2.5"}
            ${isActive
              ? "bg-[#4682B4] text-white"
              : "bg-[#4A4A4A] hover:bg-[#5A5A5A] text-white"}
          `}
        >
          <span className="flex-shrink-0 text-base leading-none">
            <IconComponent size={16} />
          </span>
          {!collapsed && (
            <span className="text-xs sm:text-sm truncate flex-1 leading-snug">
              {item.label}
            </span>
          )}
        </NavLink>

        {collapsed && tooltipVisible && (
          <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 z-[200] bg-gray-800 text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap pointer-events-none">
            {item.label}
            <span className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-800" />
          </div>
        )}
      </li>
    );
  }

  // ---- Item with a submenu (Job Management, Inventory). ----
  const onModulePath = location.pathname === item.path;
  const filterKeys = childFilterKeys(item.children);
  const isChildActive = (child) => {
    if (!onModulePath) return false;
    const childQs = child.to.includes("?") ? child.to.split("?")[1] : "";
    const childParams = new URLSearchParams(childQs);
    const childHasFilter = [...filterKeys].some((k) => childParams.get(k));
    if (!childHasFilter) {
      // The "All" link — active only when none of the submenu's filter params
      // are present in the current URL.
      const cur = new URLSearchParams(location.search);
      return [...filterKeys].every((k) => !cur.get(k));
    }
    // A quick-view link — active when every filter param it sets matches the
    // current URL (order-independent for comma lists), ignoring page/q/pageSize.
    return [...filterKeys].every((k) =>
      sameSet(parseCsvParam(location.search, k), parseCsvParam(childQs, k))
    );
  };

  const childLink = (child, padding) => (
    <NavLink
      key={child.label}
      to={child.to}
      onClick={onNavigate}
      className={`block rounded-md text-xs sm:text-sm transition-colors ${padding} ${
        isChildActive(child)
          ? "bg-[#4682B4] text-white font-medium shadow-sm"
          : "text-gray-300 hover:bg-[#4A4A4A] hover:text-white"
      }`}
    >
      {child.label}
    </NavLink>
  );

  const moduleActive = onModuleTree(location.pathname, item.path);

  return (
    <li
      className="relative"
      onMouseEnter={() => collapsed && setTooltipVisible(true)}
      onMouseLeave={() => setTooltipVisible(false)}
    >
      {/* Parent row: label click opens all jobs; caret toggles the submenu. */}
      <div
        className={`flex items-center rounded-md transition-colors ${
          moduleActive ? "bg-[#4682B4]" : "bg-[#4A4A4A] hover:bg-[#5A5A5A]"
        }`}
      >
        <NavLink
          to={item.path}
          end={false}
          onClick={onNavigate}
          title={collapsed ? item.label : undefined}
          className={`flex-1 flex items-center gap-3 cursor-pointer text-left text-white
            ${collapsed ? "justify-center px-0 py-2.5" : "px-3 py-2.5"}`}
        >
          <span className="flex-shrink-0 text-base leading-none">
            <IconComponent size={16} />
          </span>
          {!collapsed && (
            <span className="text-xs sm:text-sm truncate flex-1 leading-snug">
              {item.label}
            </span>
          )}
        </NavLink>
        {!collapsed && (
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-label={`${open ? "Collapse" : "Expand"} ${item.label}`}
            aria-expanded={open}
            className="px-2 py-2.5 text-white/80 hover:text-white flex-shrink-0"
          >
            {open ? <FaChevronDown size={11} /> : <FaChevronRight size={11} />}
          </button>
        )}
      </div>

      {/* Expanded: inline sub-items. */}
      {!collapsed && open && (
        <ul className="mt-1 mb-1.5 ml-[1.65rem] pl-2.5 border-l-2 border-[#4A4A4A] space-y-0.5">
          {item.children.map((child) => (
            <li key={child.label}>{childLink(child, "px-3 py-1.5")}</li>
          ))}
        </ul>
      )}

      {/* Collapsed: hover flyout. pl-2 bridges the gap so the hover survives the
          pointer moving from the icon into the flyout (both are one DOM subtree). */}
      {collapsed && tooltipVisible && (
        <div className="absolute left-full top-0 pl-2 z-[200]">
          <div className="bg-[#3A3A3A] rounded-md shadow-xl py-1 min-w-[168px] border border-[#5A5A5A]">
            <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
              {item.label}
            </div>
            {item.children.map((child) => childLink(child, "px-3 py-1.5"))}
          </div>
        </div>
      )}
    </li>
  );
}
